/* feel-cart-drawer.js — ported from 03_CART_DRAWER_PIECES/cart-drawer-script.html
   Cart API: Shopify AJAX Cart API (cart/change.js, cart/add.js, cart.js)
   Opener:   [data-cart-open] on header cart button (already wired in header.liquid)
*/
(() => {

  const backdrop = document.querySelector('[data-cd-backdrop]');
  const drawer   = document.querySelector('[data-cd-drawer]');
  if (!backdrop || !drawer) return;

  /* Translated strings (from window.FA_CART_STRINGS in feel-cart-drawer.liquid).
     Tokens {amount}/{x} are substituted at runtime. */
  const FA = window.FA_CART_STRINGS || {};

  const closeBtn     = drawer.querySelector('[data-cd-close]');
  const itemsEl      = drawer.querySelector('[data-cd-items]');
  const emptyEl      = drawer.querySelector('[data-cd-empty]');
  const recsEl       = drawer.querySelector('[data-cd-recs]');
  const discountEl   = drawer.querySelector('[data-cd-discount]');
  const subtotalEl   = drawer.querySelector('[data-cd-subtotal]');
  const savingsRow   = drawer.querySelector('[data-cd-savings-row]');
  const savingsEl    = drawer.querySelector('[data-cd-savings]');
  const totalEl      = drawer.querySelector('[data-cd-total]');
  const checkoutPriceEl = drawer.querySelector('[data-cd-checkout-price]');
  const checkoutBtn  = drawer.querySelector('.cd-checkout');
  const progressFill = drawer.querySelector('[data-cd-progress-fill]');
  const progressMsg  = drawer.querySelector('[data-cd-progress-msg]');
  const msShipping   = drawer.querySelector('[data-cd-ms-shipping]');

  const cfg = window.cartConfig || {};
  const SHIPPING_CENTS        = cfg.shippingCents        || 5400;
  const PROTECTION_VARIANT_ID = cfg.protectionVariantId  || 56780238061892;
  const routes = window.routes || {};

  let triggerEl = null;
  let planDataMap = {};

  // -------- Money formatting --------
  function fmtMoney(cents) {
    const n = (cents / 100).toFixed(2);
    // Use store money_format if available; fallback to €X.XX
    if (cfg.moneyFormat) {
      return cfg.moneyFormat.replace('{{amount}}', n)
                             .replace('{{amount_with_comma_separator}}', n.replace('.', ','))
                             .replace('{{amount_no_decimals}}', Math.round(cents / 100));
    }
    return '€' + n;
  }

  // -------- Open / Close --------
  function openDrawer(trigger) {
    triggerEl = trigger || document.activeElement;
    backdrop.dataset.open = 'true';
    drawer.dataset.open   = 'true';
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert'); // a11y: focusable again when open
    document.body.style.overflow = 'hidden';
    document.body.classList.add('feel-drawer-open'); // hides floating chat launcher (overlaps checkout CTA)
    setTimeout(() => closeBtn?.focus(), 80);
  }

  function closeDrawer() {
    backdrop.dataset.open = 'false';
    drawer.dataset.open   = 'false';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', ''); // a11y: remove closed drawer from tab order (aria-hidden-focus)
    document.body.style.overflow = '';
    document.body.classList.remove('feel-drawer-open');
    if (triggerEl && typeof triggerEl.focus === 'function') triggerEl.focus();
    triggerEl = null;
  }

  // Expose global API (useful for add-to-cart JS to call cartDrawer.open())
  window.cartDrawer = { open: openDrawer, close: closeDrawer };

  // Wire openers: [data-cart-open] (header cart button)
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open], .cart-toggle, .cart-badge');
    if (opener) { e.preventDefault(); openDrawer(opener); }
  });

  closeBtn?.addEventListener('click', closeDrawer);
  backdrop.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.dataset.open === 'true') closeDrawer();
  });

  // Focus trap
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || drawer.dataset.open !== 'true') return;
    const focusable = drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  });

  // -------- Cart AJAX helpers --------
  async function cartChange(key, quantity, sellingPlan = null) {
    const url = (routes.cart_change_url || '/cart/change') + '.js';
    const body = { id: key, quantity };
    if (sellingPlan !== null) body.selling_plan = sellingPlan;
    const r = await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify(body)
    });
    if (!r.ok) throw new Error('cartChange failed: ' + r.status);
    return r.json();
  }

  async function cartAdd(variantId, qty = 1) {
    const url = (routes.cart_add_url || '/cart/add') + '.js';
    const r = await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify({ id: variantId, quantity: qty })
    });
    if (!r.ok) throw new Error('cartAdd failed: ' + r.status);
    return r.json();
  }

  async function getCart() {
    const r = await fetch('/cart.js');
    return r.json();
  }

  async function cartUpdateDiscount(code) {
    const url = (routes.cart_update_url || '/cart/update') + '.js';
    const r = await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify({ discount: code })
    });
    if (!r.ok) throw new Error('cartUpdateDiscount failed: ' + r.status);
    return r.json();
  }

  // -------- Escape HTML for JS-built strings --------
  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // -------- Selling plan map: built from Liquid-rendered DOM on init --------
  function buildPlanMap() {
    planDataMap = {};
    drawer.querySelectorAll('[data-cd-sub-plan-id]').forEach(el => {
      const handle   = el.dataset.productHandle;
      const planId   = el.dataset.cdSubPlanId;
      const discount = el.dataset.cdSubDiscount;
      if (handle && planId && planId !== '0') {
        planDataMap[handle] = { planId, discount: Number(discount) };
      }
    });
  }

  // -------- Re-render line items from cart JSON --------
  function renderItems(cart) {
    if (!itemsEl) return;
    const visibleItems = cart.items.filter(item => item.variant_id !== PROTECTION_VARIANT_ID);
    itemsEl.innerHTML = visibleItems.map(item => {
      /* For selling-plan items, Shopify sets original_price = subscription price
         (already discounted). The full unit price lives in
         selling_plan_allocation.compare_at_price. Use whichever is higher. */
      const spCompare   = item.selling_plan_allocation
        ? (item.selling_plan_allocation.compare_at_price || 0) : 0;
      const comparePrice = Math.max(item.original_price, spCompare);
      const discounted  = comparePrice > item.final_price;
      const savingsPct  = discounted
        ? Math.round((comparePrice - item.final_price) / comparePrice * 100)
        : 0;
      const variantLine = (item.variant_title && item.variant_title !== 'Default Title')
        ? `<p class="cd-item-plan">${esc(item.variant_title)}</p>` : '';
      const planLine = (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan)
        ? `<p class="cd-item-plan">${esc(item.selling_plan_allocation.selling_plan.name)}</p>` : '';
      const imgTag = item.image
        ? `<img src="${esc(item.image)}" alt="${esc(item.title)}" loading="lazy" width="64" height="80">` : '';
      const strikeTag = discounted
        ? `<span class="strike">${fmtMoney(comparePrice)}</span>` : '';
      const saveTpl = FA.save_percent || 'Save {x}%';
      const savingsTag = discounted
        ? `<span class="cd-item-savings">${saveTpl.replace('{x}', savingsPct)}</span>` : '';
      const decDisabled = item.quantity <= 1 ? ' disabled' : '';

      // Upsell / revert button (plan data sourced from planDataMap, built from Liquid DOM)
      const planEntry = planDataMap[item.handle] || {};
      const planId    = planEntry.planId   || 0;
      const planDisc  = planEntry.discount || 0;
      const isSubbed  = !!item.selling_plan_allocation;
      const hasplan   = !isSubbed && planId && String(planId) !== '0';
      let upsellHTML  = '';
      if (isSubbed) {
        /* FA strings are already HTML-escaped by Liquid's `| t`; don't re-esc(). */
        const switchLabel = FA.switch_one_time || 'Switch to one-time';
        upsellHTML = `<button type="button" class="cd-item-revert" data-cd-revert-trigger
        aria-label="${switchLabel}">${switchLabel}</button>`;
      } else if (hasplan) {
        const subTpl = FA.subscribe_save || 'Subscribe &amp; save {x}%';
        const subLabel = planDisc > 0
          ? subTpl.replace('{x}', planDisc)
          : subTpl.replace('{x} %', '').replace('{x}%', '').replace('{x}', '').trim();
        upsellHTML = `<button type="button" class="cd-item-upsell" data-cd-upsell-trigger
        aria-label="${subLabel}">${subLabel}</button>`;
      }

      return `
<div class="cd-item" data-cd-item
     data-line-key="${esc(item.key)}"
     data-product-handle="${esc(item.handle)}"
     data-variant-id="${esc(String(item.variant_id))}"
     data-cd-sub-plan-id="${esc(String(planId))}"
     data-cd-sub-discount="${esc(String(planDisc))}">
  <div class="cd-item-image">${imgTag}</div>
  <div class="cd-item-body">
    <div class="cd-item-row">
      <h3 class="cd-item-name">${esc(item.product_title)}</h3>
      <button type="button" class="cd-item-remove" data-cd-remove
              aria-label="Remove ${esc(item.title)}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
        </svg>
      </button>
    </div>
    ${variantLine}${planLine}
    <div class="cd-item-meta">
      <div class="cd-stepper" role="group" aria-label="Quantity">
        <button type="button" aria-label="Decrease quantity" data-cd-qty-dec${decDisabled}>−</button>
        <span class="qty">${item.quantity}</span>
        <button type="button" aria-label="Increase quantity" data-cd-qty-inc>+</button>
      </div>
      <div class="cd-item-meta-right">
        ${savingsTag}
        <div class="cd-item-price">${strikeTag}${fmtMoney(item.final_price)}</div>
      </div>
    </div>
    ${upsellHTML}
  </div>
</div>`;
    }).join('');
  }

  // -------- Update totals, progress, badge, empty state --------
  function updateTotals(cart) {
    // original_total_price = sum of (item.original_price × qty) — captures selling plan discounts.
    // savings = original_total_price - total_price covers ALL discount sources.
    // Invariant: subtotal - savings = total (always true, clean visual math).
    const subtotal  = cart.original_total_price;
    const savings   = cart.original_total_price - cart.total_price;
    const cartTotal = cart.total_price;

    // Protection is now a real line item — total already includes it
    const displayTotal = cartTotal;

    // innerHTML required: fmtMoney may return HTML (e.g. <span class="glc-money">€X</span>)
    if (subtotalEl)      subtotalEl.innerHTML      = fmtMoney(subtotal);
    if (savingsEl)       savingsEl.innerHTML       = '−' + fmtMoney(savings);
    if (totalEl)         totalEl.innerHTML         = fmtMoney(displayTotal);
    if (checkoutPriceEl) checkoutPriceEl.innerHTML = '· ' + fmtMoney(displayTotal);
    if (savingsRow) savingsRow.style.display = savings > 0 ? 'flex' : 'none';

    updateProgress(cartTotal);
    updateBadge(cart.item_count);
    updateEmpty(cart.item_count);
    updateDiscountDisplay(cart);
  }

  function updateProgress(totalCents) {
    const pct = Math.min(100, (totalCents / SHIPPING_CENTS) * 100);
    if (progressFill) progressFill.style.width = pct + '%';
    if (msShipping) msShipping.classList.toggle('reached', totalCents >= SHIPPING_CENTS);
    if (progressMsg) {
      if (totalCents >= SHIPPING_CENTS) {
        progressMsg.innerHTML = FA.unlocked_shipping || 'You’ve unlocked free shipping.';
      } else {
        var shipTpl = FA.progress_shipping || 'Add {amount} more for free shipping.';
        progressMsg.innerHTML = shipTpl.replace('{amount}', '<strong>' + fmtMoney(SHIPPING_CENTS - totalCents) + '</strong>');
      }
    }
  }

  function updateBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.classList.toggle('feel-header__cart-badge--hidden', count === 0);
    });
  }

  function updateEmpty(count) {
    const empty = count === 0;
    if (emptyEl)      emptyEl.hidden = !empty;
    if (itemsEl)      itemsEl.style.display      = empty ? 'none' : '';
    if (recsEl)       recsEl.style.display        = empty ? 'none' : '';
    if (discountEl)   discountEl.style.display    = empty ? 'none' : '';
    if (checkoutBtn) {
      if (empty) {
        checkoutBtn.setAttribute('disabled', '');
      } else {
        checkoutBtn.removeAttribute('disabled');
      }
    }
  }

  // -------- Discount code display --------
  function updateDiscountDisplay(cart) {
    const codeApps = (cart.discount_applications || []).filter(d => d.type === 'discount_code');
    let   codeRow  = drawer.querySelector('[data-cd-discount-code-row]');
    const summary  = drawer.querySelector('.cd-summary');

    if (codeApps.length > 0) {
      const d      = codeApps[0];
      const amount = d.total_allocated_amount || 0;
      if (!codeRow && summary) {
        codeRow = document.createElement('div');
        // Reuse .cd-savings style (rose colour) + identify with data attr
        codeRow.className = 'cd-summary-row cd-savings cd-discount-code';
        codeRow.setAttribute('data-cd-discount-code-row', '');
        const totalRow = summary.querySelector('.cd-total');
        if (totalRow) summary.insertBefore(codeRow, totalRow);
        else           summary.appendChild(codeRow);
      }
      if (codeRow) {
        codeRow.innerHTML = '<span>Code <strong>' + esc(d.code || d.title || '—')
                          + '</strong></span><span>−' + fmtMoney(amount) + '</span>';
        codeRow.style.display = '';
      }
    } else if (codeRow) {
      codeRow.style.display = 'none';
    }
  }

  function showDiscountError(msg) {
    const discSection = drawer.querySelector('[data-cd-discount]');
    if (!discSection) return;
    let errEl = discSection.querySelector('[data-cd-discount-error]');
    if (!errEl) {
      errEl = document.createElement('p');
      errEl.setAttribute('data-cd-discount-error', '');
      errEl.style.cssText = 'font-size:0.75rem;color:var(--rose-700,#c0145c);margin:4px 0 0;padding:0;';
      discSection.appendChild(errEl);
    }
    errEl.textContent = msg;
    // Open the accordion so the error is visible
    if (discSection.dataset.open !== 'true') {
      discSection.dataset.open = 'true';
      discSection.querySelector('[data-cd-discount-toggle]')?.setAttribute('aria-expanded', 'true');
    }
  }

  function clearDiscountError() {
    const errEl = drawer.querySelector('[data-cd-discount-error]');
    if (errEl) errEl.textContent = '';
  }

  function setLoading(itemEl, on) {
    if (itemEl) itemEl.style.opacity = on ? '0.5' : '';
  }

  async function upgradeItem(itemEl) {
    const key    = itemEl.dataset.lineKey;
    const qtyEl  = itemEl.querySelector('.qty');
    const planId = itemEl.dataset.cdSubPlanId;
    if (!key || !qtyEl || !planId || planId === '0') return;
    setLoading(itemEl, true);
    try {
      const cart = await cartChange(key, parseInt(qtyEl.textContent, 10), planId);
      renderItems(cart);
      updateTotals(cart);
    } catch { setLoading(itemEl, false); }
  }

  async function revertItem(itemEl) {
    const key       = itemEl.dataset.lineKey;
    const variantId = itemEl.dataset.variantId;
    const qtyEl     = itemEl.querySelector('.qty');
    if (!key || !variantId || !qtyEl) return;
    const qty = parseInt(qtyEl.textContent, 10);
    setLoading(itemEl, true);
    try {
      await cartChange(key, 0);
      await cartAdd(variantId, qty);
      const cart = await getCart();
      renderItems(cart);
      updateTotals(cart);
    } catch { setLoading(itemEl, false); }
  }

  // -------- Event delegation on drawer root --------
  drawer.addEventListener('click', async (e) => {

    // Quantity decrease
    const dec = e.target.closest('[data-cd-qty-dec]');
    if (dec) {
      const item  = dec.closest('[data-cd-item]');
      const key   = item?.dataset.lineKey;
      const qtyEl = item?.querySelector('.qty');
      if (!key || !qtyEl) return;
      const newQty = Math.max(0, parseInt(qtyEl.textContent, 10) - 1);
      setLoading(item, true);
      try {
        const cart = await cartChange(key, newQty);
        renderItems(cart);
        updateTotals(cart);
      } catch { setLoading(item, false); }
      return;
    }

    // Quantity increase
    const inc = e.target.closest('[data-cd-qty-inc]');
    if (inc) {
      const item  = inc.closest('[data-cd-item]');
      const key   = item?.dataset.lineKey;
      const qtyEl = item?.querySelector('.qty');
      if (!key || !qtyEl) return;
      const newQty = parseInt(qtyEl.textContent, 10) + 1;
      setLoading(item, true);
      try {
        const cart = await cartChange(key, newQty);
        renderItems(cart);
        updateTotals(cart);
      } catch { setLoading(item, false); }
      return;
    }

    // Remove item
    const remove = e.target.closest('[data-cd-remove]');
    if (remove) {
      const item = remove.closest('[data-cd-item]');
      const key  = item?.dataset.lineKey;
      if (!key) return;
      setLoading(item, true);
      try {
        const cart = await cartChange(key, 0);
        renderItems(cart);
        updateTotals(cart);
      } catch { setLoading(item, false); }
      return;
    }

    // Subscribe upsell
    const upsell = e.target.closest('[data-cd-upsell-trigger]');
    if (upsell) {
      const item = upsell.closest('[data-cd-item]');
      if (item) await upgradeItem(item);
      return;
    }

    // Revert to one-time
    const revert = e.target.closest('[data-cd-revert-trigger]');
    if (revert) {
      const item = revert.closest('[data-cd-item]');
      if (item) await revertItem(item);
      return;
    }

    // Recommended add
    const recAdd = e.target.closest('[data-cd-rec-add]');
    if (recAdd) {
      const li        = recAdd.closest('[data-variant-id]');
      const variantId = li?.dataset.variantId;
      if (!variantId) return;
      recAdd.disabled    = true;
      recAdd.textContent = 'Adding…';
      try {
        await cartAdd(variantId);
        const cart = await getCart();
        renderItems(cart);
        updateTotals(cart);
        li.remove(); // item is now in cart — remove from recs
      } catch {
        recAdd.disabled    = false;
        recAdd.textContent = 'Add';
      }
      return;
    }

    // Discount toggle
    const discToggle = e.target.closest('[data-cd-discount-toggle]');
    if (discToggle) {
      e.preventDefault();
      e.stopPropagation();
      const disc   = discToggle.closest('[data-cd-discount]');
      const isOpen = disc?.dataset.open === 'true';
      if (disc) disc.dataset.open = isOpen ? 'false' : 'true';
      discToggle.setAttribute('aria-expanded', String(!isOpen));
      if (!isOpen) setTimeout(() => disc?.querySelector('.cd-discount-input')?.focus(), 50);
      return;
    }

    // Discount apply — AJAX via /cart/update.js (no redirect, mirrors live feelawsm.com)
    const discApply = e.target.closest('[data-cd-discount-apply]');
    if (discApply) {
      const input = drawer.querySelector('.cd-discount-input');
      const code  = input?.value.trim();
      if (!code) return;
      discApply.disabled    = true;
      discApply.textContent = 'Applying…';
      clearDiscountError();
      try {
        const result = await cartUpdateDiscount(code);
        const cart   = await getCart();
        renderItems(cart);
        updateTotals(cart);          // calls updateDiscountDisplay
        // Validate: applicable=false with items in cart → code rejected
        const applied = (result.discount_codes || [])[0];
        if (applied && !applied.applicable && cart.item_count > 0) {
          showDiscountError('Code not valid');
        }
      } catch {
        showDiscountError('Something went wrong');
      } finally {
        discApply.disabled    = false;
        discApply.textContent = 'Apply';
      }
      return;
    }

  });

  // Buy-box ATC: refresh cart contents and open drawer
  document.addEventListener('feel:cart-updated', async function () {
    const cart = await getCart();
    renderItems(cart);
    updateTotals(cart);
    openDrawer(null);
  });

  // Initial state: build plan map from Liquid DOM, then sync badge + totals
  buildPlanMap();
  // Clear any leftover sessionStorage flags from previous redirect-based approach
  try { sessionStorage.removeItem('feel:reopen-cart'); sessionStorage.removeItem('feel:discount-tried'); } catch {}
  getCart().then(cart => {
    updateTotals(cart);           // also calls updateDiscountDisplay
  });

})();
